import { useAdminAuth } from "@auth/AuthContext"
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react"

import { isPlatformSuperuser } from "@/constants/roles"

// The ONE institution every page in the console is looking at.
//
// An INSTITUTION_ADMIN / VIEWER is locked to their own. A PLATFORM_SUPERUSER
// picks one in the header (persisted per browser) and every page — the
// dashboard included — follows it. Before this existed each page had its own
// picker with a different default and the dashboard ignored all of them.
const SCOPE_KEY = "platonica_admin_institution_scope"

interface InstitutionScope {
  institutionId: string
  setInstitutionId: (id: string) => void
  /** True when the current admin may change the scope (platform superuser). */
  canSwitch: boolean
}

const Ctx = createContext<InstitutionScope | null>(null)

function readStored(): string {
  try {
    return localStorage.getItem(SCOPE_KEY) ?? ""
  } catch {
    return ""
  }
}

export function InstitutionScopeProvider({ children }: { children: ReactNode }) {
  const { admin } = useAdminAuth()
  const canSwitch = isPlatformSuperuser(admin?.role)
  const own = admin?.institution.id ?? ""
  const [stored, setStored] = useState(readStored)

  const setInstitutionId = useCallback((id: string) => {
    setStored(id)
    try {
      localStorage.setItem(SCOPE_KEY, id)
    } catch {
      // Private mode / blocked storage: the choice just doesn't persist.
    }
  }, [])

  const value = useMemo<InstitutionScope>(
    () => ({
      institutionId: canSwitch ? stored || own : own,
      setInstitutionId,
      canSwitch,
    }),
    [canSwitch, stored, own, setInstitutionId],
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useInstitutionScope(): InstitutionScope {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error("useInstitutionScope must be used inside InstitutionScopeProvider")
  return ctx
}
